import type {
  ApiRequestHeaders,
  ApiRequestMethod,
  ApiRequestOptions,
  ApiResponseBase,
  ApiResponseState,
} from './client.types'
import { UPLOAD_TIMEOUT_MS } from './config'
import { headersBase } from './headers'
import { ResponseStatusCodes } from './responseStatusCodes'
import { toResponseError } from './toResponseError'

/** First status outside the 2xx success range. */
const REDIRECT_STATUS_FROM = 300
const PROGRESS_COMPLETE_PERCENT = 100
/** 100 is reserved for a successful response — raw byte transfer finishing first would be misleading. */
const PROGRESS_TRANSFER_MAX_PERCENT = 99
/** XHR exposes no HTTP status for transport failures. */
const TRANSPORT_ERROR_CODE = 0

function parseErrorBody(responseText: string): ApiResponseBase | undefined {
  try {
    return JSON.parse(responseText)
  } catch {
    return undefined
  }
}

/**
 * XMLHttpRequest transport, used only when a caller needs upload progress: `fetch`
 * exposes none, and its streaming alternative requires the server to speak HTTP/2.
 * Resolves the same `ApiResponseState` as `apiRequest` so callers cannot tell them apart.
 */
export function xhrRequest<T, B = unknown>(
  requestURL: URL,
  method: ApiRequestMethod,
  body: B,
  headers: ApiRequestHeaders,
  options?: ApiRequestOptions,
  timeout: number = UPLOAD_TIMEOUT_MS,
): Promise<ApiResponseState<T>> {
  return new Promise((resolve) => {
    const state: ApiResponseState<T> = {
      isOk: false,
      isError: false,
      isAuthError: false,
      isInvalid: false,
      data: undefined,
      error: undefined,
      responseStatus: undefined,
    }

    function fail(error: unknown): void {
      const responseError = error as NonNullable<ApiResponseState<T>['error']>
      state.error = responseError
      state.isError = true
      state.isAuthError =
        state.responseStatus === ResponseStatusCodes.UNAUTHORIZED ||
        responseError?.code === ResponseStatusCodes.UNAUTHORIZED
      resolve(Object.freeze(state))
    }

    const xhr = new XMLHttpRequest()
    xhr.open(method, requestURL.href)
    xhr.timeout = timeout
    xhr.withCredentials = options?.credentials === 'include'

    // Content-Type is deliberately never set, so the browser derives the multipart boundary
    const requestHeaders = headersBase(!!options?.webview, headers)
    Object.entries(requestHeaders).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value)
    })

    const { onUploadProgress } = options ?? {}
    if (onUploadProgress) {
      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) return
        const percent = Math.round((event.loaded / event.total) * PROGRESS_COMPLETE_PERCENT)
        onUploadProgress(Math.min(percent, PROGRESS_TRANSFER_MAX_PERCENT))
      }
    }

    xhr.onload = () => {
      state.responseStatus = xhr.status
      if (xhr.status < ResponseStatusCodes.OK || xhr.status >= REDIRECT_STATUS_FROM) {
        fail(toResponseError(parseErrorBody(xhr.responseText), xhr.status, xhr.statusText))
        return
      }

      let result: Record<string, unknown>
      try {
        const parsed: unknown = JSON.parse(xhr.responseText)
        // `JSON.parse` accepts bare literals, so the shape has to be checked before any property read
        if (parsed === null || typeof parsed !== 'object') {
          throw new Error('Malformed response body')
        }
        result = parsed as Record<string, unknown>
      } catch (parseError) {
        fail(parseError)
        return
      }

      if (result.error) {
        state.isInvalid = true
        fail(result.error)
        return
      }

      state.isOk = true
      state.data = result as T & ApiResponseBase
      onUploadProgress?.(PROGRESS_COMPLETE_PERCENT)
      resolve(Object.freeze(state))
    }

    xhr.onerror = () => fail({ code: TRANSPORT_ERROR_CODE, message: 'Network error' })
    xhr.ontimeout = () => fail({ code: TRANSPORT_ERROR_CODE, message: 'Request timed out' })
    xhr.onabort = () => fail({ code: TRANSPORT_ERROR_CODE, message: 'Request aborted' })

    if (method === 'GET' || method === 'HEAD') {
      xhr.send()
      return
    }

    xhr.send(body instanceof FormData ? body : JSON.stringify(body))
  })
}
